import classNames from 'classnames';
import DurationFormat from 'components/DurationFormat';
import DurationRealtimeFormat from 'components/DurationRealtimeFormat';
import { ClockIcon, LoggedIcon, SmallBrain } from 'components/Icons';
import RichTooltip from 'components/RichTooltip';
import { SUGGESTED_TIME_TOOLTIP } from 'constants/hour';
import { DURATION_FORMAT } from 'constants/memory';
import type { HourTotals } from 'factories/calendar';
import { motion } from 'framer-motion';
import React, { Component } from 'react';
import FontAwesome from 'react-fontawesome';
import type { HourAsCard } from 'types/hour';
import type { SuggestionAsCard } from 'types/suggestion';
import type { TagBase } from 'types/tag';
import type { Merge } from 'types/utility';
import styles from './Hour.module.scss';
import HourSummaryTooltip from './HourSummaryTooltip';

const variants = {
  enter: {
    opacity: 1,
    width: 16,
    height: 16,
    flip: true,
  },
  exit: {
    opacity: 0,
    width: 0,
    height: 0,
    flip: true,
  },
};

type CardIntersection = Partial<Merge<SuggestionAsCard, HourAsCard>>;

export class HourClock extends Component<{
  onLightBackground?: boolean;
  hour: HourAsCard | SuggestionAsCard;
  isResizing: boolean | null | undefined;
  onStopTimer: () => void;
  onAcceptSuggestedDuration: (seconds: number) => void;
  showSuggestedDuration?: boolean;
  tags?: Array<TagBase>;
  hourTotals?: HourTotals;
}> {
  handleStopTimer = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.props.onStopTimer();
  };

  handleClickSuggestedTime = (e: React.MouseEvent) => {
    e.stopPropagation();
    const { numCalculatedSeconds = 0 } = this.props.hour as CardIntersection;
    this.props.onAcceptSuggestedDuration(numCalculatedSeconds);
  };
  render() {
    const { hour, showSuggestedDuration, tags } = this.props;
    const { isTimerRunning, numLoggedSecondsExact, numPlannedSeconds, timerRunningSince, ui } =
      hour;
    const showTimer = !!isTimerRunning;

    const { loggedDurationMemoriesMismatch, numCalculatedSeconds = numLoggedSecondsExact } =
      hour as CardIntersection;

    return (
      <>
        {showTimer && (
          <RichTooltip
            fixed
            position="top-end"
            tooltip="Timer is running. Click to stop"
            onClick={this.handleStopTimer}
            childrenClassName={styles.tooltipWrapper}
          >
            <motion.div initial="exit" animate="enter" variants={variants}>
              <ClockIcon />
            </motion.div>
            <DurationRealtimeFormat
              className={classNames(styles.duration, styles.staticWidthDuration)}
              value={numLoggedSecondsExact}
              valueType="seconds"
              format="h[h] mm[m] ss[s]"
              trim
              stopTrim="m"
              timerStartedOn={timerRunningSince}
            />
          </RichTooltip>
        )}
        {ui.hasPlannedTime && !numCalculatedSeconds && (
          <span>
            <FontAwesome name="calendar" className={styles.faIcon} />
            <DurationFormat
              className={styles.duration}
              value={numPlannedSeconds}
              format={DURATION_FORMAT}
              trim
              stopTrim="m"
              valueType="seconds"
            />
          </span>
        )}
        {ui.showLogged && (
          <>
            {loggedDurationMemoriesMismatch && showSuggestedDuration && (
              <>
                <RichTooltip
                  fixed
                  position="top-end"
                  tooltip={SUGGESTED_TIME_TOOLTIP}
                  onClick={this.handleClickSuggestedTime}
                  childrenClassName={styles.suggestedTime}
                >
                  <SmallBrain />
                  <DurationFormat
                    className={styles.duration}
                    highlightChange
                    value={numCalculatedSeconds}
                    format={DURATION_FORMAT}
                    trim
                    stopTrim="m"
                    valueType="seconds"
                  />
                </RichTooltip>
                <LoggedIcon />
              </>
            )}
            {ui.isSuggestion && (
              <RichTooltip fixed position="top-end" tooltip="Suggested time">
                <DurationFormat
                  className={styles.duration}
                  value={numLoggedSecondsExact}
                  format={DURATION_FORMAT}
                  trim
                  stopTrim="m"
                  valueType="seconds"
                />
              </RichTooltip>
            )}
            {!ui.isSuggestion && (
              <HourSummaryTooltip
                hourTotals={this.props.hourTotals}
                suggestedTimeNotice={ui.isSuggestion ? SUGGESTED_TIME_TOOLTIP : undefined}
                clientId={hour.clientId}
                clientName={hour.clientName}
                projectId={hour.projectId}
                projectName={hour.projectName}
                hourDay={hour.day}
                tags={tags}
                forecastId={(hour as HourAsCard).forecastId}
              >
                <DurationFormat
                  className={styles.duration}
                  highlightChange={!ui.isSuggestion && !this.props.isResizing}
                  value={numLoggedSecondsExact}
                  format={DURATION_FORMAT}
                  trim
                  stopTrim="m"
                  valueType="seconds"
                />
              </HourSummaryTooltip>
            )}
          </>
        )}
      </>
    );
  }
}
